import React from "react";
import { Bell } from "lucide-react";
import "./NotificationPanel.css";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_NOTIFICATION_READ,
  DELETE_ALL_NOTIFICATIONS,
} from "../../actions/types";
import userService from "../../services/user.service";

const formatTime = (isoString) => {
  if (!isoString) return "";
  const now  = new Date();
  const time = new Date(isoString);
  const diff = Math.floor((now - time) / 1000);
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const NotificationPanel = ({ onClose }) => {
  const { notifications } = useSelector((state) => state.notificationsReducer);
  const dispatch = useDispatch();
  const history = useHistory();

  const employeeId = (() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw)?.result?.[0]?.user?.id : null;
    } catch { return null; }
  })();

  const handleMarkAllRead = async () => {
    dispatch({ type: MARK_ALL_NOTIFICATIONS_READ });
    if (employeeId) {
      try { await userService.markAllNotificationsRead(employeeId); } catch (_) {}
    }
  };

  const handleMarkRead = async (notifId) => {
    dispatch({ type: MARK_NOTIFICATION_READ, payload: notifId });
    try { await userService.markNotificationRead(notifId); } catch (_) {}
  };

  const handleClearAll = async () => {
    dispatch({ type: DELETE_ALL_NOTIFICATIONS });
    if (employeeId) {
      try { await userService.deleteAllNotifications(employeeId); } catch (_) {}
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.isRead) {
      await handleMarkRead(n.id);
    }

    const type = n.notificationType;
    const rawData = n.data;

    if (type === "Task") {
      const taskId = parseInt(rawData, 10);
      if (!isNaN(taskId)) {
        if (onClose) onClose();
        history.push("/admin/task/view", { id: taskId });
      }
      return;
    }

    if (type === "PlannerSubmission" || type === "PlannerApproval" || type === "PlannerRejection") {
      try {
        const parsed = JSON.parse(rawData);
        const { employeeId: planEmployeeId, month, year } = parsed;
        if (onClose) onClose();
        history.push("/admin/Planner", {
          fromNotification: true,
          employeeId: planEmployeeId,
          month,
          year,
          viewSubordinate: type === "PlannerSubmission",
        });
      } catch (_) {
        if (onClose) onClose();
        history.push("/admin/Planner");
      }
      return;
    }
  };

  return (
    <div className="notif-panel">
      <div className="notif-panel__header">
        <h6 className="notif-panel__title">Notifications</h6>
        {notifications && notifications.length > 0 && (
          <div className="notif-panel__actions">
            <button className="notif-action-btn" onClick={handleMarkAllRead}>
              Mark all read
            </button>
            <button
              className="notif-action-btn notif-action-btn--danger"
              onClick={handleClearAll}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {!notifications || notifications.length === 0 ? (
        <div className="notif-panel__empty">
          <Bell size={32} className="notif-empty__icon" />
          <p className="notif-empty__text">No notifications yet</p>
        </div>
      ) : (
        <div className="notif-panel__list">
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`notif-card${n.isRead ? " notif-card--read" : ""}`}
              onClick={() => handleNotificationClick(n)}
              style={{ cursor: "pointer" }}
            >
              <div className="notif-card__icon-wrap">
                <Bell size={16} className="notif-card__bell" />
              </div>
              <div className="notif-card__body">
                <div className="notif-card__top">
                  <span className="notif-card__title">{n.title}</span>
                  <span className="notif-card__time">{formatTime(n.time)}</span>
                </div>
                <p className="notif-card__desc">{n.body}</p>
              </div>
              {!n.isRead && <span className="notif-card__dot" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;

